import { useCallback, useEffect, useRef, useState } from 'react'
import { Order } from '../types/order'
import {
  GeoPoint,
  GeocodeResult,
  RESTAURANT_ADDRESS,
  geocode,
  geocodeKeyOf,
  geocodeOrder,
} from '../lib/geocode'

/**
 * Coordinates for the delivery board's orders, resolved in the background. Returns a
 * lookup with three answers: a point; `null` = looked up, nothing found (or no usable
 * postcode to look up); `undefined` = not answered yet. The board's "Locating…" hint
 * counts only the last kind. Keyed on the address, not the order, so a poll that swaps
 * order objects costs nothing. Caching, de-dup and failure backoff all live in
 * lib/geocode.ts; this hook only turns promises into state.
 */
export const useGeocodedOrders = (orders: Order[]) => {
  const [resolved, setResolved] = useState<Record<string, GeocodeResult | null>>({})
  // Keys with a request in flight — so a poll tick between the request and its answer
  // does not attach a second round of handlers to the same lookup.
  const requested = useRef(new Set<string>())

  useEffect(() => {
    for (const order of orders) {
      const key = geocodeKeyOf(order)
      if (!key || key in resolved || requested.current.has(key)) continue

      requested.current.add(key)
      geocodeOrder(order)
        .then(
          // A state update after unmount is a no-op in React 18 — no guard needed.
          result => setResolved(prev => ({ ...prev, [key]: result })),
          () => undefined // Transport failure or backoff: stays `undefined`, retried later.
        )
        .finally(() => requested.current.delete(key))
    }
  }, [orders, resolved])

  return useCallback(
    (order: Order): GeocodeResult | null | undefined => {
      const key = geocodeKeyOf(order)
      if (!key) return null
      return key in resolved ? resolved[key] : undefined
    },
    [resolved]
  )
}

/** The restaurant's own door, for the map's home pin. null until resolved. */
export const useRestaurantLocation = (): GeoPoint | null => {
  const [home, setHome] = useState<GeoPoint | null>(null)
  useEffect(() => {
    geocode(RESTAURANT_ADDRESS.postcode, RESTAURANT_ADDRESS.houseNumber)
      .then(result => {
        if (result) setHome({ lat: result.lat, lng: result.lng })
      })
      .catch(() => undefined) // No home pin this session; the order pins still render.
  }, [])
  return home
}
